import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { MlyGrid } from './shared/MlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMonthlyData } from '../../hooks/useMonthlyData'
import { useMonthlyNormals } from '../../hooks/useMonthlyNormals'

const SLUG = 'mly-teploty'

const ROW_GROUPS = [
  {
    label: 'Průměrná teplota',
    rows: [
      { tsId: 'TEMPAVG_AVG', label: 'Průměr průměrných teplot',    unit: '°C', normalMode: 'diff', decimals: 1 },
      { tsId: 'TEMPAVG_MAX', label: 'Maximum průměrných teplot',   unit: '°C', normalMode: null,   decimals: 1 },
      { tsId: 'TEMPAVG_MIN', label: 'Minimum průměrných teplot',   unit: '°C', normalMode: null,   decimals: 1 },
    ],
  },
  {
    label: 'Minimální teplota',
    rows: [
      { tsId: 'TNMIN_AVG', label: 'Průměr minimálních teplot',    unit: '°C', normalMode: 'diff', decimals: 1 },
      { tsId: 'TNMIN_MIN', label: 'Absolutní minimum',             unit: '°C', normalMode: null,   decimals: 1 },
      { tsId: 'TNMIN_MAX', label: 'Maximum minimálních teplot',    unit: '°C', normalMode: null,   decimals: 1 },
    ],
  },
  {
    label: 'Maximální teplota',
    rows: [
      { tsId: 'TXMAX_AVG', label: 'Průměr maximálních teplot',    unit: '°C', normalMode: 'diff', decimals: 1 },
      { tsId: 'TXMAX_MAX', label: 'Absolutní maximum',             unit: '°C', normalMode: null,   decimals: 1 },
      { tsId: 'TXMAX_MIN', label: 'Minimum maximálních teplot',    unit: '°C', normalMode: null,   decimals: 1 },
    ],
  },
  {
    label: 'Povrchová teplota',
    rows: [
      { tsId: 'TGROUNDMIN_MIN', label: 'Min. povrchová teplota', unit: '°C', normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Dny tepla',
    rows: [
      { tsId: 'TXMAX_GE25',      label: 'Letní dny – Tmax ≥ 25 °C',          unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_GE30',      label: 'Horké dny – Tmax ≥ 30 °C',           unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_GE35',      label: 'Velmi horké dny – Tmax ≥ 35 °C',     unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNMIN_GE20',      label: 'Tropické noci – Tmin ≥ 20 °C',       unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNNIGHTMIN_GE20', label: 'Tropické noci (noční min.)',          unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Dny mrazu',
    rows: [
      { tsId: 'TNMIN_L0',    label: 'Mrazové dny – Tmin < 0 °C',          unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNMIN_L-10',  label: 'Silné mrazy – Tmin < −10 °C',         unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_L0',    label: 'Ledové dny – Tmax < 0 °C',            unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_LE-10', label: 'Extrémní ledové dny – Tmax ≤ −10 °C', unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka (veličiny = úzké sloupce → kratší popisy).
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  TEMPAVG_AVG: 'Průměr', TEMPAVG_MAX: 'Maximum', TEMPAVG_MIN: 'Minimum',
  TNMIN_AVG: 'Průměr', TNMIN_MIN: 'Abs. min', TNMIN_MAX: 'Maximum',
  TXMAX_AVG: 'Průměr', TXMAX_MAX: 'Abs. max', TXMAX_MIN: 'Minimum',
  TGROUNDMIN_MIN: 'Min. povrchová',
  TXMAX_GE25: 'Letní ≥ 25', TXMAX_GE30: 'Horké ≥ 30', TXMAX_GE35: 'Velmi horké ≥ 35',
  TNMIN_GE20: 'Trop. noci ≥ 20', TNNIGHTMIN_GE20: 'Trop. noci (noč. min.)',
  TNMIN_L0: 'Mrazové < 0', 'TNMIN_L-10': 'Silné mrazy < −10',
  TXMAX_L0: 'Ledové < 0', 'TXMAX_LE-10': 'Extr. ledové ≤ −10',
}, {
  'Průměrná teplota': 'Prům. teplota',
  'Minimální teplota': 'Min. teplota',
  'Maximální teplota': 'Max. teplota',
  'Povrchová teplota': 'Povrch. teplota',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function MlyTeplotyView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, yearMap, dtMap, yearDtMap, isLoading: dLoading, isError: dError } = useMonthlyData(station?.gh_id, year, TS_IDS)
  const { normalsMap, yearNormalsMap, isLoading: nLoading }        = useMonthlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <MlyGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            yearMap={yearMap}
            dtMap={dtMap}
            yearDtMap={yearDtMap}
            normalsMap={normalsMap}
            yearNormalsMap={yearNormalsMap}
          />
        )}
      </div>
    </div>
  )
}
